import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SingAlongService, SingAlongPass } from '../services/SingAlongService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';
import { trackInitiateCheckout } from '../lib/analytics';
import { Clock, Music, Users, Trophy, Loader as Loader2, CircleCheck as CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';

export const SingAlongPlans: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [searchParams] = useSearchParams();
  const [passes, setPasses] = useState<SingAlongPass[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasActivePurchase, setHasActivePurchase] = useState(false);

  const canceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user) {
        navigate(`/${basePath}/signup`, { replace: true });
        return;
      }
      if (!user.email_confirmed_at) {
        navigate(`/${basePath}/signup`, { replace: true });
        return;
      }

      try {
        const [passData, purchase] = await Promise.all([
          SingAlongService.getPasses(),
          SingAlongService.getActivePurchase(),
        ]);
        setPasses(passData);
        if (purchase) {
          setHasActivePurchase(true);
          if (purchase.league_id) {
            navigate(`/${basePath}/dashboard`, { replace: true });
          } else {
            navigate(`/${basePath}/setup`, { replace: true });
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, basePath]);

  const handlePurchase = async (passId: string) => {
    setPurchasing(passId);
    setError(null);
    const pass = passes.find((p) => p.id === passId);
    if (pass) {
      trackInitiateCheckout(pass.name, pass.price_cents);
    }
    try {
      const url = await SingAlongService.createCheckout(passId, basePath);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setPurchasing(null);
    }
  };

  const getBundleText = (limit: number | null) => {
    if (limit === null) return 'Full song catalog';
    return `${limit} song bundle${limit > 1 ? 's' : ''}`;
  };

  const getDurationText = (hours: number) => {
    if (hours <= 24) return '24 hours';
    if (hours <= 72) return '3 days';
    if (hours <= 168) return '7 days';
    return '30 days';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-600/10 via-transparent to-blue-600/10 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="inline-flex items-center gap-2 text-sm font-medium bg-white/5 border border-white/15 text-white/70 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition mb-8"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-black mb-3">Choose Your Pass</h1>
          <p className="text-white/50 text-lg">Select the perfect plan for your sing-along event</p>
        </div>

        {canceled && (
          <div className="mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-center text-sm">
            Checkout was canceled. You can try again whenever you're ready.
          </div>
        )}

        {error && (
          <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-center text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {passes.map((pass) => (
            <div
              key={pass.id}
              className={`relative flex flex-col bg-white/[0.03] border rounded-2xl p-6 transition-all hover:border-orange-500/40 ${
                pass.tag ? 'border-orange-500/50 shadow-lg shadow-orange-500/10' : 'border-white/10'
              }`}
            >
              {pass.tag && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                  {pass.tag}
                </div>
              )}
              <h3 className="font-bold text-lg mb-1">{pass.name}</h3>
              <div className="text-3xl font-black mb-1">
                ${(pass.price_cents / 100).toFixed(2)}
              </div>
              <p className="text-sm text-white/40 mb-5">one-time</p>

              <div className="space-y-3 text-sm text-white/60 flex-1">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-orange-400 shrink-0" />
                  {getDurationText(pass.duration_hours)} access
                </div>
                <div className="flex items-center gap-2">
                  <Music size={14} className="text-orange-400 shrink-0" />
                  {getBundleText(pass.bundle_limit)}
                </div>
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-orange-400 shrink-0" />
                  Unlimited participants
                </div>
                <div className="flex items-center gap-2">
                  <Trophy size={14} className="text-orange-400 shrink-0" />
                  Live leaderboard + AI scoring
                </div>
              </div>

              <button
                onClick={() => handlePurchase(pass.id)}
                disabled={!!purchasing}
                className="mt-6 w-full py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-500/25 disabled:opacity-50"
              >
                {purchasing === pass.id ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    Select
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {passes.length === 0 && !error && (
          <div className="text-center py-16 text-white/40">
            <p className="text-lg">No passes available at the moment. Check back soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingAlongPlans;
